import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { ChipSelectOption, ChipSelectVariant, variantConfig } from './chipVariants';

export interface ChipSelectProps {
  options: ChipSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  pinnedOptionValue?: string;
  variant?: ChipSelectVariant;
}

export const ChipSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchable = false,
  searchPlaceholder = 'Search...',
  pinnedOptionValue,
  variant = 'lumen',
}: ChipSelectProps) => {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || placeholder;
  const styles = useMemo(() => variantConfig[variant] ?? variantConfig['lumen'], [variant]);

  const panelStyle = useMemo(
    () => ({
      backgroundColor: styles.panelBg,
      border: `2px solid ${styles.panelBorder}`,
      borderRadius: '20px',
      padding: searchable ? '8px 12px' : '4px 12px',
    }),
    [styles, searchable],
  );

  const defaultOptionStyle = useMemo(() => ({ backgroundColor: styles.panelBg }), [styles.panelBg]);

  const sortedOptions = useMemo(
    () =>
      searchable
        ? options.toSorted((a, b) => {
            if (a.value === pinnedOptionValue) return -1;
            if (b.value === pinnedOptionValue) return 1;
            return a.label.localeCompare(b.label);
          })
        : options,
    [options, pinnedOptionValue, searchable],
  );

  const displayedOptions = useMemo(() => {
    if (!searchable) return sortedOptions;
    const search = searchValue.trim().toLowerCase();
    if (!search) return sortedOptions;
    return sortedOptions.filter((o) => o.label.toLowerCase().includes(search));
  }, [searchable, searchValue, sortedOptions]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
    setSearchValue('');
  }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    const list = searchable ? sortedOptions : options;
    const idx = list.findIndex((o) => o.value === value);
    setFocusedIndex(idx >= 0 ? idx : 0);
  }, [searchable, sortedOptions, options, value]);

  const handleToggle = useCallback(() => {
    if (open) closePanel();
    else openPanel();
  }, [open, openPanel, closePanel]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      closePanel();
      triggerRef.current?.focus();
    },
    [onChange, closePanel],
  );

  const handleOptionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleSelect(e.currentTarget.value);
    },
    [handleSelect],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setFocusedIndex(0);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setFocusedIndex(0);
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!open) openPanel();
          else setFocusedIndex((i) => Math.min(i + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (open) setFocusedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (!open) {
            openPanel();
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex].value);
          }
          break;
        case ' ':
          if (!searchable) {
            e.preventDefault();
            if (!open) openPanel();
          }
          break;
        case 'Escape':
          if (open) {
            e.preventDefault();
            closePanel();
            triggerRef.current?.focus();
          }
          break;
      }
    },
    [open, openPanel, closePanel, handleSelect, displayedOptions, focusedIndex, searchable],
  );

  const handleTriggerKeyDownSearchable = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        openPanel();
      } else if (open && e.key === 'Escape') {
        e.preventDefault();
        closePanel();
        triggerRef.current?.focus();
      }
    },
    [open, openPanel, closePanel],
  );

  useEffect(() => {
    if (open && searchable) {
      inputRef.current?.focus();
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    document.getElementById(`${panelId}-option-${focusedIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [focusedIndex, open, panelId]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closePanel]);

  const activeDescendantId =
    open && focusedIndex >= 0 && focusedIndex < displayedOptions.length
      ? `${panelId}-option-${focusedIndex}`
      : undefined;

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        aria-activedescendant={!searchable ? activeDescendantId : undefined}
        onClick={handleToggle}
        onKeyDown={searchable ? handleTriggerKeyDownSearchable : handleKeyDown}
        className={`inline-flex cursor-pointer items-center gap-6 rounded-full border-2 px-12 py-2 text-sm leading-tight font-medium whitespace-nowrap ${styles.buttonClass}`}
      >
        {selectedLabel}
        <PalmettoIcon name="chevronDown" size="xs" className="text-text-tertiary" />
      </button>
      {open && (
        <div className="mt-1 absolute top-full left-0 z-50 min-w-[180px]" style={panelStyle}>
          {searchable && (
            <div className="relative mb-4">
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={panelId}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-activedescendant={activeDescendantId}
                value={searchValue}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="px-5 w-full border-b-2 border-b-border-subtle bg-transparent py-2 pr-8 text-sm text-text-primary outline-none"
              />
              <button
                type="button"
                disabled={!searchValue}
                onClick={handleClearSearch}
                className={`absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer hover:opacity-50 ${!searchValue ? 'cursor-not-allowed opacity-30' : ''}`}
                aria-label="Clear search"
              >
                <PalmettoIcon name="x" size="xs" />
              </button>
            </div>
          )}
          <div id={panelId} role="listbox" className={searchable ? 'max-h-[240px] overflow-y-auto' : undefined}>
            {displayedOptions.map((option, index) => {
              const isSelected = option.value === value;
              const isFocused = index === focusedIndex;
              const optionClassName = isSelected
                ? styles.selectedOptionClassName
                : isFocused
                  ? 'bg-canvas-highlight-default text-text-primary'
                  : 'text-text-primary';
              return (
                <button
                  key={option.value}
                  id={`${panelId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  value={option.value}
                  onClick={handleOptionClick}
                  className={`py-2.5 relative w-full cursor-pointer rounded-full border-none px-12 text-left text-sm hover:opacity-60 ${optionClassName}`}
                  style={isSelected || isFocused ? undefined : defaultOptionStyle}
                >
                  {option.label}
                </button>
              );
            })}
            {searchable && displayedOptions.length === 0 && (
              <div className="px-5 py-2.5 text-sm text-text-tertiary">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
